import { z } from "zod";

/* This is a representation of old Lizard client JSON favourites as a Zod schema. */

/* Not using the geometry schemas from ../geojson.ts, because
   sometimes in old Lizard favourites the coordinates are actually
   strings. */

const pointSchema = z.object({
  type: z.literal("Point"),
  coordinates: z.array(z.number().or(z.string())),
});

const lineStringSchema = z.object({
  type: z.literal("LineString"),
  coordinates: z.array(z.array(z.number().or(z.string()))),
});

const polygonSchema = z.object({
  type: z.literal("Polygon"),
  coordinates: z.array(z.array(z.array(z.number().or(z.string())))),
});

const multiPolygonSchema = z.object({
  type: z.literal("MultiPolygon"),
  coordinates: z.array(z.array(z.array(z.array(z.number().or(z.string()))))),
});

const geometrySchema = z.discriminatedUnion("type", [
  pointSchema,
  lineStringSchema,
  polygonSchema,
  multiPolygonSchema,
]);
export type LegacyGeometry = z.infer<typeof geometrySchema>;

export const featureSchema = z.object({
  type: z.literal("Feature"),
  geometry: geometrySchema,
  properties: z.record(z.string(), z.any()).optional(),
});

/* Geometries in the old client were either Geojson Geometries (where
   any number could suddenly be a string), or Geojson features with
   extra fields right in the object rather than in the properties
   record. Also sometimes as numbers, sometimes as strings and, very
   rarely, suddenly an object... */

export const legacyGeometrySchema = z.union([
  z.object({ geometry: geometrySchema }),
  featureSchema.extend({
    /* These should have been in the properties record */
    id: z.number().optional(),
    area: z.number().or(z.string()).optional(),
    code: z.string().optional(),
    name: z.string().optional(),
    raster: z
      .string()
      .or(
        z
          .object({
            data: z.number(),
            class: z.number(),
            color: z.string(),
            label: z.string(),
            fraction: z.number().optional(),
          })
          .strict()
      )
      .nullable()
      .optional(),
    regionType: z.string().optional(),
  }),
]);

/* Version of the V1 schema without VERSION field */
export const oldV1StateSchema = z.object({
  VERSION: z.literal(undefined),
  box: z.object({
    type: z.string(),
  }),
  assets: z.array(z.string()).optional(),
  layers: z.array(
    z.object({
      name: z.string().optional(),
      type: z.string(),
      uuid: z.string().optional(),
      active: z.boolean().default(true),
      opacity: z.number().or(z.string()).optional(),
      scenario: z.string().optional(),
      fetching: z.boolean().optional() /* Ignore */,
      $$hashKey: z.string().optional() /* Ignore */,
      vectorized: z.boolean().optional() /* ?? */,
      category: z.number().nullable().optional(),
      present: z.boolean().optional(),
      legendUrl: z.string().nullable().optional(),
      asset_type: z.string().optional(),
      organisation_uuid: z.string().optional(),
    })
  ),
  context: z.literal("charts").or(z.literal("map")),
  spatial: z
    .object({
      view: z
        .object({
          lat: z.string().or(z.number()),
          lng: z.string().or(z.number()),
          zoom: z.number(),
        })
        .optional(),
      bounds: z.object({
        _northEast: z
          .object({
            lat: z.string().or(z.number()),
            lng: z.string().or(z.number()),
          })
          .optional(),
        _southWest: z
          .object({
            lat: z.string().or(z.number()),
            lng: z.string().or(z.number()),
          })
          .optional(),
      }),
      userHere: z.object({}).optional(),
      mapMoving: z.boolean().optional(),
    })
    .optional(),
  language: z.string().default("en"),
  temporal: z.object({
    at: z.number().or(z.string()).optional(),
    end: z.number().or(z.string()).optional(),
    now: z.number().nullable().optional(),
    start: z.number().or(z.string()),
    playing: z.boolean().optional(),
    buffering: z.boolean().optional(),
    timelineMoving: z.boolean().optional(),
    relative: z.boolean().optional(),
    showingTemporalData: z.boolean().optional(),
  }),
  baselayer: z.string(),
  layerGroups: z
    .object({
      all: z.array(z.string()),
      active: z.array(z.string()),
      gettingData: z.boolean(),
      timeIsSyncing: z.boolean(),
    })

    .optional(),
  geometries: z.array(legacyGeometrySchema).optional(),
  layerKeys: z.array(z.string()).optional() /* IMPORTANT, these are the actual layers */,
  annotations: z
    .object({
      /* Ignore */
      active: z.boolean().optional(),
      present: z.boolean(),
    })

    .optional(),
  mouseOnLine: z.number().or(z.string()).nullable().optional() /* Ignore */,
  selectedForAssets: z
    .record(
      z.string(),
      z.object({
        timeseries: z.object({
          name: z.string(),
          unit: z.string().nullable(),
          uuid: z.string(),
          scale: z.string().nullable(),
          linked: z.boolean().nullable().optional(),
          location: z.string().optional(),
          parameter: z.string().nullable(),
          value_type: z.string(),
          compartment: z.string().nullable().optional(),
          reference_frame: z.string().nullable().optional(),
          $$hashKey: z.string().optional(),
        }),
      })
    )
    .optional(),
  selected: z
    .object({
      assets: z.array(z.string()),
      geometries: z.array(legacyGeometrySchema),
      mouseOnLine: z.number().or(z.string()).nullable().optional(),
      timeseries: z.array(
        z.object({
          uuid: z.string(),
          color: z.string(),
          order: z.number(),
          active: z.boolean(),
          measureScale: z.string().nullable().optional(),
          $$hashKey: z.string().optional(),
        })
      ),
    })

    .optional(),
  selections: z
    .array(
      z.object({
        type: z.string(),
        geom: z.string().optional(),
        asset: z.string().optional(),
        raster: z.string().optional(),
        uuid: z.string(),
        color: z.string().optional(),
        order: z.number(),
        active: z.boolean(),
        timeseries: z.string().optional(),
        measureScale: z.string().nullable().optional(),
        $$hashKey: z.string().optional(),
      })
    )
    .optional(),
});

/* The same but with VERSION: 1 */
export const v1StateSchema = oldV1StateSchema.extend({
  VERSION: z.literal(1),
});

export type V1FavouriteState = z.infer<typeof v1StateSchema>;

/* V2 schemas added chart composition */

export const v2StateSchema = v1StateSchema.extend({
  VERSION: z.literal(2),
  chartComposition: z.object({
    /* Array of arrays: which data item is in which chart */
    composedCharts: z.array(z.array(z.string())),
    dashboardCharts: z.record(
      z.string(),
      z.object({
        uuid: z.string() /* Same as record key, not a real uuid */,
        type: z.union([z.literal("raster"), z.literal("timeseries")]),
        unit: z.string().nullable(),
        asset: z.string().optional(),
        color: z.string(),
        graphType: z
          .union([
            z.literal("temporalBar"),
            z.literal("temporalLine"),
            z.literal("text"),
            z.literal("image"),
            z.literal("integer"),
          ])
          .optional(),
        valueType: z
          .union([z.literal("float"), z.literal("image"), z.literal("integer"), z.literal("text")])
          .optional(),
        timeseries: z.string().optional(),
        raster: z.string().optional(),
        description: z.string().optional(),
        measureScale: z.string().nullable(),
        reference_frame: z.string().nullable().optional(),
        geometry: geometrySchema.optional(),
      })
    ),
  }),
});
export type V2FavouriteState = z.infer<typeof v2StateSchema>;
export type ChartComposition = V2FavouriteState["chartComposition"];

export const oldFavouriteStateSchema = z.discriminatedUnion("VERSION", [
  oldV1StateSchema,
  v1StateSchema,
  v2StateSchema,
]);

export const oldFavouriteDetailSchema = z.object({
  uuid: z.string(),
  url: z.string(),
  name: z.string(),
  state: oldFavouriteStateSchema,
});

export type OldFavourite = z.infer<typeof oldFavouriteDetailSchema>;
export type OldFavouriteState = z.infer<typeof oldFavouriteStateSchema>;
